import type { CorrezioneManuale, InSala, Intervallo, PresenzaIscritto, PresenzaMia } from '@aulaviva/shared';
import type { z } from 'zod';
import type { riepilogoPresenzeSchema } from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginato } from './admin';
import { richiesta } from './client';

export type RiepilogoPresenze = z.infer<typeof riepilogoPresenzeSchema>;

export const chiaviPresenze = {
  mia: (id: string) => ['presenze', 'mia', id] as const,
  inSala: (id: string) => ['presenze', 'in-sala', id] as const,
  iscritti: (id: string, q: string, pagina: number) => ['presenze', 'iscritti', id, q, pagina] as const,
  riepilogo: (id: string) => ['presenze', 'riepilogo', id] as const,
  intervalli: (id: string, userId: string) => ['presenze', 'intervalli', id, userId] as const,
};

// ---- partecipante
export function usePresenzaMia(id: string, abilitata = true, intervalloMs = 60_000) {
  return useQuery({
    queryKey: chiaviPresenze.mia(id),
    queryFn: async () => (await richiesta<PresenzaMia>(`/me/webinar/${id}/presenza`)).dati,
    enabled: abilitata,
    refetchInterval: intervalloMs,
  });
}
export function useSala(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (azione: 'ingresso' | 'uscita') => (await richiesta<PresenzaMia>(`/webinar/${id}/sala/${azione}`, { method: 'POST' })).dati,
    onSuccess: (p) => qc.setQueryData(chiaviPresenze.mia(id), p),
  });
}

// ---- admin
function invalidaPresenze(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['presenze'] });
}
export function useInSala(id: string, intervalloMs = 10_000) {
  return useQuery({ queryKey: chiaviPresenze.inSala(id), queryFn: async () => (await richiesta<InSala[]>(`/admin/webinar/${id}/in-sala`)).dati, refetchInterval: intervalloMs });
}
export function usePresenzeIscritti(id: string, q: string, pagina: number, perPagina = 50, intervalloMs = 10_000) {
  const p = new URLSearchParams({ pagina: String(pagina), perPagina: String(perPagina) });
  if (q) p.set('q', q);
  return useQuery({
    queryKey: chiaviPresenze.iscritti(id, q, pagina),
    queryFn: async () => (await richiesta<Paginato<PresenzaIscritto>>(`/admin/webinar/${id}/presenze?${p.toString()}`)).dati,
    refetchInterval: intervalloMs,
    placeholderData: (prec) => prec,
  });
}
export function useRiepilogoPresenze(id: string, abilitato = true, intervalloMs = 10_000) {
  return useQuery({
    queryKey: chiaviPresenze.riepilogo(id),
    queryFn: async () => (await richiesta<RiepilogoPresenze>(`/admin/webinar/${id}/presenze/riepilogo`)).dati,
    enabled: abilitato,
    refetchInterval: intervalloMs,
  });
}
export function useIntervalli(id: string, userId: string | null) {
  return useQuery({
    queryKey: chiaviPresenze.intervalli(id, userId ?? ''),
    queryFn: async () => (await richiesta<Intervallo[]>(`/admin/webinar/${id}/presenze/${userId}/intervalli`)).dati,
    enabled: Boolean(userId),
  });
}
export function useCorrezioneManuale(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: CorrezioneManuale) => (await richiesta<Intervallo>(`/admin/webinar/${id}/presenze/manuale`, { method: 'POST', json: c })).dati,
    onSuccess: () => invalidaPresenze(qc),
  });
}
export function useChiudiIntervallo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ intervalloId, fine, nota }: { intervalloId: string; fine?: string; nota: string }) =>
      (await richiesta<Intervallo>(`/admin/presenze/intervalli/${intervalloId}/chiudi`, { method: 'POST', json: fine ? { fine, nota } : { nota } })).dati,
    onSuccess: () => invalidaPresenze(qc),
  });
}
export function useAnnullaIntervallo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ intervalloId, nota }: { intervalloId: string; nota: string }) => (await richiesta<Intervallo>(`/admin/presenze/intervalli/${intervalloId}/annulla`, { method: 'POST', json: { nota } })).dati,
    onSuccess: () => invalidaPresenze(qc),
  });
}
export function useRicalcolaPresenze(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<RiepilogoPresenze>(`/admin/webinar/${id}/presenze/ricalcola`, { method: 'POST' })).dati,
    onSuccess: () => invalidaPresenze(qc),
  });
}
